"use client";

import React from "react";
import { Hexagon, Layers, Activity, Shield, Droplets, Zap, Anchor, Sparkles } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const glossaryItems = [{
  term: "Enamel",
  definition: "The hard, outer surface layer of your teeth that serves to protect against tooth decay. It is the hardest mineral substance in your body.",
  icon: Hexagon,
  span: "col-span-1 md:col-span-2 md:row-span-2",
  gradient: "from-cyan-200/40 via-blue-100/20 to-transparent",
  glowColor: "bg-cyan-400/20",
  shapeClass: "rotate-12 scale-150"
}, {
  term: "Dentin",
  definition: "The layer underneath the enamel. It is softer than enamel and contains microscopic tubules. When enamel wears down, these tubules can transmit pain signals.",
  icon: Layers,
  span: "col-span-1 md:row-span-2",
  gradient: "from-amber-200/40 via-orange-100/20 to-transparent",
  glowColor: "bg-amber-400/20",
  shapeClass: "-rotate-6 scale-125"
}, {
  term: "Pulp",
  definition: "The center of the tooth containing living connective tissue and cells called odontoblasts. It is the vital part of the tooth.",
  icon: Activity,
  span: "col-span-1",
  gradient: "from-rose-200/40 via-pink-100/20 to-transparent",
  glowColor: "bg-rose-400/20",
  shapeClass: "scale-110"
}, {
  term: "Gingiva",
  definition: "Commonly known as gums. The mucosal tissue that lies over the mandible and maxilla inside the mouth.",
  icon: Droplets,
  span: "col-span-1 md:col-span-2",
  gradient: "from-red-200/40 via-rose-100/20 to-transparent",
  glowColor: "bg-red-400/20",
  shapeClass: "rotate-45 scale-125 translate-x-4"
}, {
  term: "Cementum",
  definition: "A specialized calcified substance covering the root of a tooth. It helps attach the tooth to the alveolar bone.",
  icon: Anchor,
  span: "col-span-1",
  gradient: "from-stone-200/40 via-gray-100/20 to-transparent",
  glowColor: "bg-stone-400/20",
  shapeClass: "-rotate-12 scale-110"
}, {
  term: "Biofilm",
  definition: "A sticky film of bacteria that forms on teeth. If not removed regularly, it hardens into tartar.",
  icon: Sparkles,
  span: "col-span-1",
  gradient: "from-emerald-200/40 via-green-100/20 to-transparent",
  glowColor: "bg-emerald-400/20",
  shapeClass: "rotate-180 scale-125"
}, {
  term: "Fluoride",
  definition: "A mineral that helps prevent tooth decay by making the tooth more resistant to acid attacks from plaque bacteria and sugars.",
  icon: Shield,
  span: "col-span-1 md:col-span-2",
  gradient: "from-indigo-200/40 via-violet-100/20 to-transparent",
  glowColor: "bg-indigo-400/20",
  shapeClass: "scale-150 -translate-y-4"
}, {
  term: "Nerve",
  definition: "Part of the pulp, the nerve provides sensation to the tooth. It reacts to stimuli like hot and cold.",
  icon: Zap,
  span: "col-span-1",
  gradient: "from-yellow-200/40 via-lime-100/20 to-transparent",
  glowColor: "bg-yellow-400/20",
  shapeClass: "rotate-12 scale-110"
}];
export default function TheVisualGlossary() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="the-visual-glossary" ref={ref} className="bg-background relative w-full overflow-hidden py-24">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="bg-accent/10 absolute -top-24 -left-24 h-96 w-96 rounded-full blur-3xl" />
        <div className="bg-primary/5 absolute top-1/2 right-0 h-[500px] w-[500px] -translate-y-1/2 rounded-full blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className={`mb-16 max-w-2xl transition-all duration-700 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <h2 className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            The Visual Glossary
          </h2>
          <p className="text-muted-foreground text-lg">
            Explore the fundamental structures of dental health through our interactive, abstract guide. Hover over any cell to uncover the science behind the smile.
          </p>
        </div>

        <div className="grid auto-rows-[180px] grid-cols-1 gap-4 md:grid-cols-4 md:gap-6">
          <TooltipProvider delayDuration={100}>
            {glossaryItems.map((item, i) => <Tooltip key={item.term}>
                <TooltipTrigger asChild>
                  <div data-index={i} className={cn("group relative overflow-hidden rounded-3xl border border-white/40 bg-white/40 p-6 shadow-sm backdrop-blur-md transition-all duration-500 hover:scale-[1.02] hover:shadow-md hover:border-white/60 dark:border-white/10 dark:bg-white/5", item.span, isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")} style={{
                transitionDelay: `${i * 100}ms`
              }}>
                    {}
                    <div className={cn("absolute inset-0 bg-gradient-to-br opacity-50 transition-opacity duration-500 group-hover:opacity-80", item.gradient)} />

                    {}
                    <div className="absolute inset-0 flex items-center justify-center opacity-30 transition-all duration-700 group-hover:scale-110 group-hover:opacity-50">
                      <div className={cn("absolute h-32 w-32 rounded-full blur-2xl", item.glowColor)} />
                      <item.icon strokeWidth={1} className={cn("text-foreground/20 h-32 w-32 transition-transform duration-700", item.shapeClass)} />
                    </div>

                    {}
                    <div className="absolute bottom-4 left-4 z-10">
                      <span className="bg-background/80 text-foreground shadow-xs backdrop-blur-xs inline-flex items-center rounded-full px-3 py-1 text-xs font-medium tracking-wide uppercase">
                        {item.term}
                      </span>
                    </div>

                    {}
                    <div className="text-foreground/10 absolute top-4 right-4">
                      <item.icon className="h-5 w-5" />
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-popover/95 text-popover-foreground border-border/50 max-w-xs p-4 backdrop-blur-xl">
                  <p className="font-semibold mb-1 text-sm">{item.term}</p>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {item.definition}
                  </p>
                </TooltipContent>
              </Tooltip>)}
          </TooltipProvider>
        </div>
      </div>
    </section>;
}